// State that belongs to one entry of the history stack — what a browser keeps
// for a page on its own (scroll position, form fields) and a React screen does
// not.
//
// A route change unmounts the screen, and everything it held in useState goes
// with it. On a fund that meant: pick the وام‌ها tab, open a loan, tap back —
// and land on اعضا again, at the top of the page. A manager who had scrolled
// eighty members down to open one record found their place gone the same way.
// Back took them to the previous page, but not to the previous step.
//
// Keyed by react-router's `location.key`, which is new on every push and comes
// back unchanged on back/forward (and across a reload, since it lives in
// `history.state`). So back returns the screen exactly as it was left, while
// opening the same fund again from home still starts it fresh.
//
// Not the URL, for two reasons. A `?tab=` survives into a fresh visit only if
// someone copies it, so it buys nothing over this. And the searches kept here
// are names and phone numbers — PostHog records `$current_url` on every event.

import {
    Dispatch,
    SetStateAction,
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react'
import { useLocation, useNavigationType } from 'react-router-dom'

const STORAGE_KEY = 'hamyan:entry-state'
/** A back stack deeper than this is not walked back through in practice. */
const MAX_ENTRIES = 30
/** The name `useEntryScroll` saves under, for a screen that wants to read it. */
export const ENTRY_SCROLL = 'scrollY'

type Entry = Record<string, unknown>
type Store = Record<string, Entry>

// sessionStorage rather than memory alone: iOS and the messenger webviews
// reload a backgrounded page, and history (with its keys) survives that. Read
// once, then mirrored — a private window that refuses storage still works for
// as long as the document lives.
let cache: Store | null = null

const load = (): Store => {
    if (!cache) {
        try {
            const raw = window.sessionStorage.getItem(STORAGE_KEY)
            cache = raw ? JSON.parse(raw) : {}
        } catch {
            cache = {}
        }
    }
    return cache as Store
}

export const readEntry = <T>(entry: string, name: string): T | undefined =>
    load()[entry]?.[name] as T | undefined

export const writeEntry = (entry: string, name: string, value: unknown) => {
    const store = load()
    const current = store[entry] ?? {}
    // Re-inserted so key order is least-recently-written first: string keys
    // keep insertion order, and the trim below drops from the front.
    delete store[entry]
    store[entry] = { ...current, [name]: value }
    const keys = Object.keys(store)
    // max(0, …): a negative end would have slice count from the back
    keys.slice(0, Math.max(0, keys.length - MAX_ENTRIES)).forEach((stale) => {
        delete store[stale]
    })
    try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(store))
    } catch {
        // full or refused: the in-memory copy still serves this document
    }
}

/** For tests: forget everything, including the in-memory copy. */
export const resetEntryState = () => {
    cache = null
    try {
        window.sessionStorage.removeItem(STORAGE_KEY)
    } catch {
        // nothing to forget
    }
}

/**
 * This visit to this screen. The pathname is part of it because the first
 * entry of every document has the same key, `default`.
 */
export const useEntryKey = () => {
    const { key, pathname } = useLocation()
    return `${key}${pathname}`
}

/**
 * `useState`, except that back/forward to this history entry gives the value
 * back as it was left. A fresh visit starts from `initial`.
 *
 * @param name unique within the screen — every component on it shares the
 *   entry, so prefix it with the screen (`fund.tab`, `fund.loanSort`).
 */
export function useEntryState<T>(
    name: string,
    initial: T
): [T, Dispatch<SetStateAction<T>>] {
    const entry = useEntryKey()
    const restore = (): T => {
        const saved = readEntry<T>(entry, name)
        return saved === undefined ? initial : saved
    }
    const [state, setState] = useState(() => ({ entry, value: restore() }))
    let current = state
    if (state.entry !== entry) {
        // Another entry of the same route — two visits to one fund, walked
        // between — is a re-render, not a remount. Without this the value of
        // the screen being left would be written over the one returned to.
        current = { entry, value: restore() }
        setState(current)
    }
    useEffect(() => {
        writeEntry(current.entry, name, current.value)
    }, [current.entry, name, current.value])
    const setValue = useCallback<Dispatch<SetStateAction<T>>>(
        (action) =>
            setState((prev) => ({
                entry: prev.entry,
                value: action instanceof Function ? action(prev.value) : action,
            })),
        []
    )
    return [current.value, setValue]
}

/**
 * Put the window back where it was when this entry was left.
 *
 * Only on back/forward: a push is a new visit and starts wherever it starts
 * today. `ready` is the screen saying its content is on the page — before
 * that there is nothing tall enough to scroll to. Lists come back from the
 * react-query cache on the first render, so in practice it is ready at once;
 * the retries cover a list that is still growing, and stop the moment the
 * member touches the screen, so a slow restore never fights them.
 */
export function useEntryScroll(ready: boolean) {
    const entry = useEntryKey()
    const navigationType = useNavigationType()
    // The entry `pending` was worked out for; see useEntryState on why the
    // entry can change under a screen that stays mounted.
    const seen = useRef<string | null>(null)
    const pending = useRef<number | null>(null)
    if (seen.current !== entry) {
        seen.current = entry
        const saved =
            navigationType === 'POP'
                ? readEntry<number>(entry, ENTRY_SCROLL)
                : undefined
        pending.current = saved && saved > 0 ? saved : null
    }

    // Layout-effect cleanup runs inside the commit that removes the screen,
    // before the next page's shorter content can clamp the scroll and fire a
    // scroll event of its own — so what is saved is where the member left it.
    useLayoutEffect(() => {
        // Mid-restore, the target is where the member is, even if the page
        // has not grown tall enough to show it yet.
        let lastY = pending.current ?? window.scrollY
        const onScroll = () => {
            lastY = window.scrollY
        }
        window.addEventListener('scroll', onScroll, { passive: true })
        return () => {
            window.removeEventListener('scroll', onScroll)
            writeEntry(entry, ENTRY_SCROLL, lastY)
        }
    }, [entry])

    useLayoutEffect(() => {
        const target = pending.current
        if (!ready || target === null) return
        let frame = 0
        const startedAt = Date.now()
        const events = ['touchstart', 'wheel', 'keydown', 'mousedown']
        const release = () => {
            cancelAnimationFrame(frame)
            events.forEach((type) => window.removeEventListener(type, stop))
        }
        function stop() {
            pending.current = null
            release()
        }
        events.forEach((type) =>
            window.addEventListener(type, stop, { passive: true })
        )
        const attempt = () => {
            window.scrollTo(0, target)
            if (
                Math.abs(window.scrollY - target) <= 1 ||
                Date.now() - startedAt > 1500
            ) {
                stop()
                return
            }
            frame = requestAnimationFrame(attempt)
        }
        attempt()
        // Unmounting mid-restore: leave `pending` set, so StrictMode's
        // re-mount in development picks the restore up again.
        return release
    }, [ready, entry])
}
